/* eslint-disable no-console */

/**
 * Conditional console logger.
 *
 * Wraps `console.debug` and `console.error` so that debug output is only
 * emitted when a flag stored in `localStorage` is truthy.
 *
 * @example
 *   const logger = new Logger({ key: 'peg_debug' })
 *   logger.debug('only shown when localStorage peg_debug is set')
 *   logger.error('always shown')
 */
export default class Logger {
  /**
   * @param {Object} options
   * @param {string} options.key - localStorage key whose presence/truthiness
   *   controls whether `debug()` output is visible.
   */
  constructor({ key } = {}) {
    this._key = key
  }

  /** @returns {boolean} Whether debug logging is enabled. */
  get _enabled() {
    try {
      return Boolean(this._key && localStorage.getItem(this._key))
    } catch {
      // localStorage may be unavailable (SSR, security restrictions)
      return false
    }
  }

  /**
   * Log a debug message (only when the localStorage flag is set).
   * @param {...any} args - Arguments forwarded to `console.log`.
   */
  debug(...args) {
    if (this._enabled) {
      console.log('%c[DEBUG]', 'color: #05adfb ;', ...args)
    }
  }

  /**
   * Log a warning message (only when the localStorage flag is set).
   * @param {...any} args - Arguments forwarded to `console.warn`.
   */
  warn(...args) {
    console.warn('%c[WARN]', 'color: #e6a700;', ...args)
  }

  /**
   * Log an error message (always emitted).
   * @param {...any} args - Arguments forwarded to `console.error`.
   */
  error(...args) {
    console.error('%c[ERROR]', 'color: #ff0000;', ...args)
  }
}
